import React, { useState, useRef } from 'react';
import { notasAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatTime } from '../services/api';

const NotaForm = ({ expedienteId, expedienteNumero, onNotaCreated, onCancel }) => {
  const { user } = useAuth();
  const [contenido, setContenido] = useState('');
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const textareaRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const archivosInvalidos = files.filter(file => file.size > 20 * 1024 * 1024);
    if (archivosInvalidos.length > 0) {
      setError(`Algunos archivos exceden el tamaño máximo de 20MB: ${archivosInvalidos.map(f => f.name).join(', ')}`);
      return;
    }

    setArchivosAdjuntos(prev => [...prev, ...files]);
    setError(null);

    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setArchivosAdjuntos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!contenido || contenido.trim() === '') {
        throw new Error('El contenido de la nota es requerido');
      }

      const formData = new FormData();
      formData.append('contenido', contenido.trim());

      archivosAdjuntos.forEach((archivo) => {
        formData.append('archivos', archivo);
      });

      await notasAPI.createNota(expedienteId, formData);

      setContenido('');
      setArchivosAdjuntos([]);
      if (textareaRef.current) {
        textareaRef.current.value = '';
      }

      if (onNotaCreated) {
        onNotaCreated();
      }
    } catch (err) {
      console.error('Error al crear nota:', err);
      setError(err.response?.data?.error || err.message || 'Error al crear la nota');
    } finally {
      setLoading(false);
    }
  };

  const fechaHora = new Date();
  const fechaFormateada = formatDate(fechaHora.toISOString());
  const horaFormateada = formatTime(fechaHora.toISOString());

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-large">
        <div className="modal-header">
          <h3>➕ Nueva Nota</h3>
          <button className="btn-close" onClick={onCancel}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="nota-form-header">
              <div className="nota-form-user">
                <i className="fas fa-user"></i>
                <strong>{user?.nombre || 'Usuario'}</strong>
                <span className="nota-form-rol">{user?.rol || ''}</span>
              </div>
              <div className="nota-form-datetime">
                <i className="fas fa-calendar-alt"></i>
                <span>{fechaFormateada} - {horaFormateada}</span>
              </div>
            </div>

            <div className="nota-form-contenido-wrapper" style={{ position: 'relative', zIndex: 10 }}>
              <textarea
                ref={textareaRef}
                className="nota-form-textarea"
                placeholder="Escribe tu nota aquí..."
                value={contenido}
                onChange={(e) => setContenido(e.target.value)}
                rows={10}
                required
              />
            </div>

            <div className="nota-form-archivo">
              <label className="btn-archivo-label">
                <i className="fas fa-paperclip"></i>
                <span>Adjuntar archivos (opcional, máximo 20MB cada uno)</span>
                <input
                  type="file"
                  onChange={handleFileChange}
                  multiple
                  style={{ display: 'none' }}
                />
              </label>

              {archivosAdjuntos.length > 0 && (
                <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {archivosAdjuntos.map((archivo, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        background: '#f0f0f0',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    >
                      <i className="fas fa-file" style={{ color: '#666' }}></i>
                      <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {archivo.name}
                      </span>
                      <span style={{ color: '#666', fontSize: '11px' }}>
                        ({(archivo.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          fontSize: '14px'
                        }}
                        title="Eliminar archivo"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="alert alert-error" style={{ marginTop: '1rem' }}>
                {error}
              </div>
            )}

          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !contenido.trim()}
            >
              {loading ? 'Guardando...' : 'Guardar Nota'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NotaForm;
